import FoldShowMore from '@components/common/FoldShowMore/FoldShowMore'
import Link from '@components/common/Link/Link'
import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import Button from '@components/ui/Button'
import Media from '@components/ui/Media/Media'

import { umbracoButtonMap } from '@lib/umbracoButtonMap'
import clsx from 'clsx'
import React, { CSSProperties, FC, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { UmbracoLink } from 'types/umbracoLink'
import { ModuleProps } from '..'

import { MediaWithCropsFragment } from '@lib/umbraco/generated/types.generated'
import invariant from 'tiny-invariant'
import styles from './HeroImage.module.scss'
type HeroImageProps = {
  image?: MediaWithCropsFragment
  richText?: string
  maxWidth?: number
  darkenBackground?: boolean
  viewHeight?: number
  ctaLink?: UmbracoLink
  primaryButtonType?: 'primary' | 'secondary' | 'link' | 'clean' | null
  secondaryButton?: UmbracoLink
  secondaryButtonType?: 'primary' | 'secondary' | 'link' | 'clean' | null
  fontColor?: string
} & ModuleProps

const HeroImage: FC<HeroImageProps> = ({ ...props }) => {
  const isVideoContain =
    props.image?.media?.__typename === 'UmbracoMediaVideo' &&
    props.image.media.videoSizing?.toLowerCase() === 'contain'

  return (
    <div
      data-module="HeroImage"
      className={styles.root}
      style={
        {
          '--user-height': props.viewHeight ? `${props.viewHeight}vh` : `100vh`,
        } as CSSProperties
      }
    >
      {(!props.viewHeight || props.viewHeight >= 100) && !isVideoContain && (
        <FoldShowMore className={styles.foldShowMore}></FoldShowMore>
      )}

      {props.ctaLink && <RenderContent {...props}></RenderContent>}
      {!props.ctaLink && <RenderContent {...props}></RenderContent>}
    </div>
  )
}

const RenderContent: FC<HeroImageProps> = ({
  ctaLink,
  maxWidth,
  image,
  darkenBackground,
  richText,
  primaryButtonType,
  secondaryButton,
  secondaryButtonType,
  fontColor,
  lcp,
}) => {
  const [btnInView, setBtnInView] = React.useState(true)
  const { ref, inView, entry } = useInView({
    /* Optional options */
    threshold: 1,
    initialInView: true,
  })
  useEffect(() => {
    if (entry) {
      if (entry.boundingClientRect.x <= 0) setBtnInView(false)
    }
  }, [entry])

  let mediaClassName
  const isVideo = image?.media?.__typename === 'UmbracoMediaVideo'

  if (isVideo) {
    invariant(
      image?.media?.__typename === 'UmbracoMediaVideo',
      'Video media type must have video data',
    )

    mediaClassName = clsx(styles.heroMedia, {
      [styles.contain]: image?.media?.videoSizing?.toLowerCase() === 'contain',
    })
  } else {
    mediaClassName = styles.heroMedia
  }

  return (
    <div className={styles.container}>
      <div className="w-full h-full">
        {ctaLink && image && (
          <Link link={ctaLink}>
            <Media
              mediaWithCrops={image}
              mediaClassName={mediaClassName}
              className={clsx(styles.heroMediaContainer, {
                [styles.darker]: darkenBackground,
              })}
              priority={lcp}
              disableShopTheLook
            />
          </Link>
        )}
        {!ctaLink && image && (
          <Media
            mediaWithCrops={image}
            mediaClassName={mediaClassName}
            className={clsx(styles.heroMediaContainer, {
              [styles.darker]: darkenBackground,
            })}
            priority={lcp}
            disableShopTheLook
          />
        )}
      </div>

      <div
        className={styles.textContainer}
        style={
          fontColor
            ? ({ '--font-color': fontColor } as CSSProperties)
            : undefined
        }
      >
        <TrustedHtml html={richText} maxWidth={maxWidth} />

        {ctaLink && (
          <div
            className={clsx('mt-8', {
              'inline-block space-x-10': btnInView,
              'flex flex-col space-y-4': !btnInView,
            })}
            ref={ref}
          >
            {/* <Link className={styles.ctaLink} link={ctaLink}>
          
          </Link> */}
            {ctaLink && (
              <Button
                variant={umbracoButtonMap(primaryButtonType)}
                className={'!inline'}
                as={Link}
                link={ctaLink}
              >
                {ctaLink.name}
              </Button>
            )}
            {secondaryButton && (
              <Button
                variant={umbracoButtonMap(secondaryButtonType)}
                className={'!inline'}
                as={Link}
                link={secondaryButton}
              >
                {secondaryButton.name}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default React.memo(HeroImage)
